import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import RotatePhoneIcon from './rotate_phone_icon'
import {lockLandscape, unlockOrientation, orientationChangeSupported} from '../utils/orientation_utils'
import {VIEW_MODES} from '../constants'
import { componentText } from '../utils/i18n_utils'

const t = (key, fallback) => componentText(`orientation_button.${key}`, fallback)

// Mobile-only button that turns the leaderboard to landscape, through the
// native app bridge where there is one and the Screen Orientation API
// otherwise. Where neither exists (iOS in a browser) it could not rotate
// anything, so it stays hidden and the coach mark carries the guidance
// on its own.
const OrientationButton = ({
  viewMode, isLocked, setIsLocked, showRotateHint, markRotateHintUsed,
}) => {
  // isLocked lives in the store, not component state: this button mounts
  // inside the keyed selected-event tile, so local state reset to false on
  // every event switch while the page stayed rotated - mislabeling the button
  // and, through the native bridge's blind toggle, reversing its action.
  useEffect(() => {
    // Track fullscreen in both directions, not just on exit: while the page is
    // fullscreen this is what keeps the button rendered (see the guard below),
    // so the way back out never disappears — including when the browser or the
    // OS entered fullscreen without going through handleToggle.
    const handleFullscreenChange = () => {
      const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement
      setIsLocked(!!isFullscreen)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [ setIsLocked ])

  const isMobileView = [ VIEW_MODES.MOBILE, VIEW_MODES.LANDSCAPE ].includes(viewMode)
  if ((!isMobileView || !orientationChangeSupported()) && !isLocked) {
    return null
  }

  const handleToggle = () => {
    markRotateHintUsed()

    if (isLocked) {
      unlockOrientation()
      setIsLocked(false)
      return
    }

    const container = document.getElementById('lbv2__leaderboard') || document.documentElement
    lockLandscape(container)
      .then(() => setIsLocked(true))
      .catch(() => showRotateHint())
  }

  return (
    <button
      type="button"
      className="lbv2__orientation-btn themed-text"
      aria-label={isLocked
        ? t('rotate_to_portrait', 'Rotate to portrait')
        : t('rotate_to_landscape', 'Rotate to landscape')}
      aria-pressed={isLocked}
      onClick={handleToggle}>
      <RotatePhoneIcon/>
    </button>
  )
}

OrientationButton.propTypes = {
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isLocked: PropTypes.bool.isRequired,
  setIsLocked: PropTypes.func.isRequired,
  showRotateHint: PropTypes.func.isRequired,
  markRotateHintUsed: PropTypes.func.isRequired,
}

export default OrientationButton
